import { checkTypeExhausted } from "Utility";
import {
  bracketIncludesMatchId,
  BracketSegment,
  getBracketMatchIds,
  getBracketMatchTitle,
} from "./Bracket/Bracket";
import {
  getQualifierMatchTitle,
  QualifierSegment,
} from "./Qualifier/Qualifier";
import { getRoundsSegmentGroupMatchTitle } from "./RoundsSegmentGroup";
import { SeasonSegment } from "./Season/Season";
import { SwissSegment } from "./Swiss/Swiss";
import { SingleSegment } from "./Single/Single";
import { TournamentSegmentGroup } from "./TournamentSegmentGroup";
import { TournamentSegmentLink } from "./TournamentSegmentLink";

export type TournamentSegmentBase<
  TType extends string,
  TGroup extends TournamentSegmentGroup
> = {
  dqForceLast: boolean | undefined;
  dqRosterIds: string[] | undefined;
  ffForceLast: boolean | undefined;
  ffRosterIds: string[] | undefined;
  groups: TGroup[];
  message: string | undefined;
  name: string;
  segmentId: string;
  type: TType;
};

export type TournamentSegment =
  | BracketSegment
  | QualifierSegment
  | SingleSegment
  | SeasonSegment
  | SwissSegment;

export const getSegmentMatchIds = (segment: TournamentSegment) => {
  switch (segment.type) {
    case "Bracket":
      return segment.groups.flatMap(getBracketMatchIds);
    case "Qualifier":
      return segment.groups.flatMap((g) => g.matchIds);
    case "Season":
    case "Swiss":
      return segment.groups.flatMap((g) => g.rounds.flatMap((r) => r.matchIds));
    case "Single":
      return segment.groups.map((g) => g.matchId);
    default:
      checkTypeExhausted(segment);
      return [];
  }
};

export const getSegmentGroupMatchIds = (
  segment: TournamentSegment,
  groupId: string
) => {
  switch (segment.type) {
    case "Bracket":
      const bracket = segment.groups.find((g) => g.groupId === groupId);
      return !!bracket ? getBracketMatchIds(bracket) : undefined;
    case "Qualifier":
      return segment.groups.find((g) => g.groupId === groupId)?.matchIds;
    case "Season":
    case "Swiss":
      return segment.groups
        .find((g) => g.groupId === groupId)
        ?.rounds.flatMap((r) => r.matchIds);
    case "Single":
      const single = segment.groups.find((g) => g.groupId === groupId);
      return !!single ? [single.matchId] : undefined;
    default:
      checkTypeExhausted(segment);
      return [];
  }
};

export const segmentContainsMatch = (
  segment: TournamentSegment,
  matchId: string
) => {
  return getSegmentMatchIds(segment).includes(matchId);
};

export const getSegmentMatchTitle = (
  segment: TournamentSegment,
  matchId: string,
  includeMatchPart: boolean
) => {
  switch (segment.type) {
    case "Bracket":
      const bracket = segment.groups.find((g) =>
        bracketIncludesMatchId(g, matchId)
      );
      return !!bracket
        ? getBracketMatchTitle(bracket, matchId, segment.name, includeMatchPart)
        : undefined;
    case "Qualifier":
      const qualifier = segment.groups.find((g) =>
        g.matchIds.includes(matchId)
      );
      return !!qualifier
        ? getQualifierMatchTitle(qualifier, matchId)
        : undefined;
    case "Season":
    case "Swiss":
      const roundGroup = segment.groups.find((g) =>
        g.rounds.flatMap((r) => r.matchIds).includes(matchId)
      );
      return !!roundGroup
        ? getRoundsSegmentGroupMatchTitle(roundGroup, matchId)
        : undefined;
    case "Single":
      if (!segmentContainsMatch(segment, matchId)) {
        return undefined;
      }

      return `${segment.name}`;
    default:
      return checkTypeExhausted(segment);
  }
};

export const selectSegmentGroupByName = <
  TType extends string,
  TGroup extends TournamentSegmentGroup,
  TSegment extends TournamentSegmentBase<TType, TGroup>
>(
  segment: TSegment,
  groupName: string
): TGroup | undefined => {
  return segment.groups.length === 1
    ? segment.groups[0]
    : segment.groups.find((g) => g.name === groupName);
};

const FILTER_SPLIT = ": ";
// If the segment is included in a segment link, returns linkName: segmentName, else returns segmentName
export const getSegmentFilterName = (
  segment: TournamentSegment,
  links: TournamentSegmentLink[] | undefined
) => {
  const link = links?.find((l) => l.segmentIds.includes(segment.segmentId));
  if (!!link) {
    return `${link.name}${FILTER_SPLIT}${segment.name}`;
  }

  return segment.name;
};

export const getSegmentFromFilterName = (
  filterName: string,
  segments: TournamentSegment[],
  links: TournamentSegmentLink[] | undefined
) => {
  // If includes the filter split then is comprised of linkName and segmentName
  if (filterName.includes(FILTER_SPLIT)) {
    if (!links) {
      return undefined;
    }

    const [linkName, segmentName] = filterName.split(FILTER_SPLIT, 2);
    const link = links.find((l) => l.name === linkName);

    if (!link) {
      return undefined;
    }

    return segments.find(
      (s) => link.segmentIds.includes(s.segmentId) && s.name === segmentName
    );
  }

  return segments.find((s) => s.name === filterName);
};
